import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import FadeIn from "@/components/FadeIn";
import SEO from "@/components/SEO";
import { useLanguage } from "@/contexts/LanguageContext";
import { ArrowRight } from "lucide-react";

const faqCategories = [
  {
    title: "General Questions",
    faqs: [
      {
        q: "What is medical tourism?",
        a: "Medical tourism involves traveling to another country to receive medical treatment. India is one of the top destinations, offering world-class healthcare at significantly lower costs compared to Western countries."
      },
      {
        q: "Why should I choose India for medical treatment?",
        a: "India offers internationally accredited hospitals, highly skilled doctors (many trained abroad), advanced medical technology, and costs that are 60-90% lower than in the US, UK, or Europe. English is widely spoken, making communication easy."
      },
      {
        q: "How does Flarial MediTours help?",
        a: "We provide end-to-end support including medical consultation, hospital selection, treatment planning, travel and visa assistance, accommodation, and post-treatment follow-up care. We handle all logistics so you can focus on your health."
      },
      {
        q: "Is it safe to get medical treatment in Kerala?",
        a: "Yes, Kerala's top hospitals are JCI and NABH accredited, meeting international safety and quality standards. Our partner hospitals have modern infrastructure, advanced equipment, and highly experienced medical professionals."
      },
    ]
  },
  {
    title: "Treatment & Hospitals",
    faqs: [
      {
        q: "How do you select the right hospital for me?",
        a: "Based on your medical condition, required treatment, budget, and preferences, our medical team recommends the best-suited hospital and specialist. We consider success rates, specialization, and patient reviews."
      },
      {
        q: "Can I get a second opinion before traveling?",
        a: "Absolutely. We arrange video consultations with specialists so you can discuss your case, ask questions, and get a second opinion before making any travel decisions."
      },
      {
        q: "What types of treatments are available?",
        a: "We cover a wide range of specialties including Cardiology, Orthopedics, Oncology, Neurology, Fertility, Cosmetic Surgery, Dental, Eye Care, and Organ Transplants among others."
      },
    ]
  },
  {
    title: "Costs & Payment",
    faqs: [
      {
        q: "How much can I save compared to my home country?",
        a: "Patients typically save 60-90% on medical treatment costs. For example, a heart bypass surgery costs around $5,000 in Kerala, India compared to $75,000 in the US. This includes hospital charges, doctor fees, and medication."
      },
      {
        q: "Are there any hidden costs?",
        a: "No. We provide transparent and detailed cost estimates upfront, covering hospital charges, doctor fees, medication, and any additional procedures. The estimate you receive is what you pay."
      },
      {
        q: "What payment methods are accepted?",
        a: "Hospitals accept international credit/debit cards, wire transfers, and cash. We help facilitate the payment process and can also assist with medical loan arrangements if needed."
      },
      {
        q: "Does insurance cover medical tourism?",
        a: "Some international insurance plans do cover medical tourism. We can help you check with your insurance provider and prepare necessary documentation for claims."
      },
    ]
  },
  {
    title: "Travel & Logistics",
    faqs: [
      {
        q: "Do I need a special visa for medical treatment?",
        a: "Yes, India offers a Medical Visa (M-Visa) specifically for patients seeking treatment. We assist with the entire visa application process, including preparing required documents and hospital invitation letters."
      },
      {
        q: "How long does the visa process take?",
        a: "Medical visa processing typically takes 3-5 working days. Express processing options are available for urgent cases. We handle the entire process for you."
      },
      {
        q: "Will someone pick me up from the airport?",
        a: "Yes. We provide airport pickup and drop-off services. A dedicated coordinator will meet you at the airport and accompany you to your accommodation or directly to the hospital if needed."
      },
      {
        q: "What about accommodation during treatment?",
        a: "We arrange accommodation near your treatment hospital, ranging from budget-friendly guest houses to luxury hotels. Options include serviced apartments for extended stays and hospital-adjacent facilities."
      },
    ]
  },
  {
    title: "After Treatment",
    faqs: [
      {
        q: "What follow-up care is provided?",
        a: "We arrange virtual follow-up consultations with your treating doctor, provide detailed care plans, coordinate with your local doctor, and offer 24/7 support for any post-treatment concerns."
      },
      {
        q: "How long should I stay in Kerala, India after treatment?",
        a: "This varies by procedure. Generally, patients stay 1-3 weeks after surgery for initial recovery and follow-up. Your doctor will advise on the recommended stay duration based on your specific case."
      },
      {
        q: "Can I travel back home immediately after treatment?",
        a: "Your doctor will clear you for travel once you're medically fit. For minor procedures, this may be within days. For major surgeries, you may need to wait 1-2 weeks. We help plan your return travel accordingly."
      },
    ]
  },
];

export default function FAQPage() {
  const { t } = useLanguage();
  const faqCats = t("faq.cats");
  const categories = Array.isArray(faqCats) ? faqCats : faqCategories;
  return (
    <div data-testid="faq-page">
      <SEO title="FAQ" description="Frequently asked questions about medical tourism in Kerala, India - treatment costs, hospital selection, visa process, travel logistics, and post-treatment care." path="/faq" keywords="medical tourism FAQ, treatment questions India, medical travel FAQ" />
      <section className="section-padding bg-[#F9F9F8]">
        <div className="max-w-4xl mx-auto">
          <FadeIn>
            <div className="text-center mb-16">
              <span className="overline">{t("faq.overline")}</span>
              <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-light text-[#03045e] mt-3 mb-6 tracking-tight">
                {t("faq.title")} <span className="font-medium text-[#00A4A9]">{t("faq.titleHighlight")}</span>
              </h1>
              <p className="text-slate-600 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">
                {t("faq.desc")}
              </p>
            </div>
          </FadeIn>

          {categories.map((cat, ci) => (
            <FadeIn key={ci} delay={ci * 80}>
              <div className="mb-10" data-testid={`faq-category-${ci}`}>
                <h2 className="font-outfit text-xl font-medium text-[#03045e] mb-4">{cat.title}</h2>
                <Accordion type="single" collapsible className="space-y-3">
                  {cat.faqs.map((faq, fi) => (
                    <AccordionItem
                      key={fi}
                      value={`${ci}-${fi}`}
                      className="bg-white border border-slate-200 rounded-xl px-6 shadow-[0_4px_20px_rgb(0,0,0,0.03)]"
                      data-testid={`faq-item-${ci}-${fi}`}
                    >
                      <AccordionTrigger className="text-sm font-medium text-[#03045e] hover:text-[#00A4A9] py-4 hover:no-underline">
                        {faq.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-sm text-slate-600 leading-relaxed pb-4">
                        {faq.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </FadeIn>
          ))}

          <FadeIn>
            <div className="bg-[#e0f2fe] rounded-2xl p-8 text-center mt-12" data-testid="faq-cta">
              <h3 className="font-outfit text-xl font-medium text-[#03045e] mb-3">
                {t("faq.stillQuestions")}
              </h3>
              <p className="text-slate-600 text-sm mb-6">
                {t("faq.stillQuestionsDesc")}
              </p>
              <Link to="/contact" data-testid="faq-contact-btn">
                <Button className="rounded-full bg-[#00A4A9] hover:bg-[#008084] text-white btn-hover">
                  Contact Us <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
